/* ==========================================================================
   On-page links that scroll without writing a # into the address bar.

   Two halves:

   1. A click on a link pointing at this same page is handled here rather than
      by the browser. The browser's own behaviour appends the fragment to the
      address bar and jumps the page; this scrolls instead and leaves the URL
      as it was, so a visitor who shares the address shares the page and not
      a position in it.

   2. Arriving with a fragment — a link from another page, such as the plan
      CTAs pointing at the booking form — still has to land in the right
      place, so the fragment is honoured once and then taken off the address
      bar. Landing is done after `load`, because images above the target move
      it while they are still arriving.

   Both halves clear the sticky header: Dawn publishes its height as
   --header-height, and the target would otherwise sit underneath it.

   The theme sets `scroll-behavior: smooth` on the root. That makes
   `behavior: 'auto'` glide, because auto means "whatever the stylesheet
   says" — so anywhere a jump is wanted, for a reduced-motion visitor or for
   a page arriving at a fragment, 'instant' is asked for by name.

   Opt out on any link with data-keep-hash.
   ========================================================================== */

(() => {
  'use strict';

  const REDUCED = matchMedia('(prefers-reduced-motion: reduce)');
  const GAP = 8; /* a little air between the header and what it points at */

  function headerOffset() {
    const raw = getComputedStyle(document.documentElement).getPropertyValue('--header-height');
    const height = parseFloat(raw);
    if (!height) return 0;
    /* only worth clearing while the header is actually over the page */
    const header = document.querySelector('.section-header');
    if (header && getComputedStyle(header).position === 'static') return 0;
    return height;
  }

  /* the element a fragment names, whether by id or by the older name attribute */
  function targetOf(hash) {
    if (!hash || hash === '#') return null;
    let id = hash.slice(1);
    try {
      id = decodeURIComponent(id);
    } catch (e) {
      /* a malformed escape: fall back to the raw text */
    }
    if (!id) return null;
    return document.getElementById(id) || document.getElementsByName(id)[0] || null;
  }

  function goTo(el, behavior) {
    const top = el.getBoundingClientRect().top + scrollY - headerOffset() - GAP;
    scrollTo({ top: Math.max(0, Math.round(top)), behavior });

    /* Keyboard and screen-reader users should carry on from the target, not
       from where the link was. A plain heading cannot hold focus, so it is
       lent a tabindex, and focusing must not scroll again. */
    if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1');
    el.focus({ preventScroll: true });
  }

  addEventListener(
    'click',
    (event) => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const link = event.target.closest?.('a[href]');
      if (!link || link.hasAttribute('download') || 'keepHash' in link.dataset) return;
      if (link.target && link.target !== '_self') return;

      const href = link.getAttribute('href') || '';
      if (!href.includes('#')) return;

      let url;
      try {
        url = new URL(link.href, location.href);
      } catch (e) {
        return;
      }
      /* a fragment on some other page is an ordinary navigation */
      if (url.origin !== location.origin) return;
      if (url.pathname !== location.pathname || url.search !== location.search) return;

      /* from here it points at this page, so the fragment never reaches the
         address bar — including a bare "#", which would otherwise jump to the
         top of the page */
      event.preventDefault();
      const el = targetOf(url.hash);
      if (el) goTo(el, REDUCED.matches ? 'instant' : 'smooth');
    },
    false
  );

  function land() {
    const el = targetOf(location.hash);
    if (!el) return;
    /* the page is arriving, so it should already be in the right place */
    goTo(el, 'instant');
    history.replaceState(null, '', location.pathname + location.search);
  }

  if (location.hash) {
    if (document.readyState === 'complete') land();
    else addEventListener('load', land, { once: true });
  }
})();
